import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import api from "../utils/api";
import { applyPreferredSportFromUser } from "../utils/fanHubProfile";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  // Configure api defaults
  useEffect(() => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
  }, [token]);

  // Check if user is authenticated on app load
  const checkAuth = useCallback(async () => {
    if (token) {
      try {
        const response = await api.get("/auth/me", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setUser(response.data.user);
        applyPreferredSportFromUser(response.data.user, window.localStorage);
      } catch (error) {
        console.error(
          "Auth check failed:",
          error.response?.data || error.message
        );
        // Don't call logout here, just set user to null
        setUser(null);
        setToken(null);
        localStorage.removeItem("token");
        delete api.defaults.headers.common["Authorization"];
      }
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [token, checkAuth]);

  const login = async (email, password) => {
    try {
      const response = await api.post("/auth/login", { email, password });
      const { token: newToken, user: userData } = response.data;

      setToken(newToken);
      setUser(userData);
      localStorage.setItem("token", newToken);
      applyPreferredSportFromUser(userData, window.localStorage);

      return { success: true };
    } catch (error) {
      console.error("Login error:", error);
      return {
        success: false,
        error: error.response?.data?.error || "Login failed",
      };
    }
  };

  const register = async (username, email, password) => {
    try {
      const response = await api.post("/auth/register", {
        username,
        email,
        password,
      });
      const { token: newToken, user: userData } = response.data;

      setToken(newToken);
      setUser(userData);
      localStorage.setItem("token", newToken);
      applyPreferredSportFromUser(userData, window.localStorage);

      return { success: true };
    } catch (error) {
      console.error("Registration error:", error);
      return {
        success: false,
        error: error.response?.data?.error || "Registration failed",
      };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
    delete api.defaults.headers.common["Authorization"];
    // Redirect to marketing page after logout
    window.location.href = "/";
  };

  const handleGoogleCallback = (token) => {
    setToken(token);
    localStorage.setItem("token", token);
    // The user data will be fetched in the useEffect when token changes
  };

  // Check if user is admin (use database role, fallback to email check)
  const isAdmin =
    user &&
    (user.isAdmin === true ||
      user.role === "admin" ||
      user.email === "jpcarvallyo@gmail.com");

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    handleGoogleCallback,
    isAuthenticated: !!user,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
